import { MesfModal } from "@dexteel/mesf-core";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import type { StraightenerRecipeVersion } from "../../straightener-recipes/models/StraightenerRecipe";

const LEAVE = "none";

interface Props {
	open: boolean;
	productLabel: string;
	recipeLabel: string;
	// Other active, complete recipes of the same product, eligible to take over
	// as the product's current.
	successors: StraightenerRecipeVersion[];
	isBusy?: boolean;
	// newCurrentRecipeId = the chosen successor, or null to leave no current.
	onConfirm: (newCurrentRecipeId: number | null) => void;
	onCancel: () => void;
}

const versionLabel = (v: StraightenerRecipeVersion) =>
	`${v.Name ?? "(unnamed)"} · v${v.Version}${v.Crew ? ` · Crew ${v.Crew}` : ""}`;

// Deactivating the product's current recipe leaves it without a current. This
// asks which of the remaining active recipes should take over (default: the most
// recent one), or to leave the product with no current. Shared by the Recipes
// manager and the Trend editor.
export const DeactivateCurrentRecipeModal = ({
	open,
	productLabel,
	recipeLabel,
	successors,
	isBusy = false,
	onConfirm,
	onCancel,
}: Props) => {
	// Newest first, so the default (top option) is the last created active recipe.
	const options = useMemo(
		() => [...successors].sort((a, b) => b.Version - a.Version),
		[successors],
	);
	const [choice, setChoice] = useState<string>(LEAVE);
	useEffect(() => {
		if (open) setChoice(options[0] ? String(options[0].Id) : LEAVE);
	}, [open, options]);

	const confirm = () => onConfirm(choice === LEAVE ? null : Number(choice));

	const hasSuccessors = options.length > 0;
	const leavesWithoutCurrent = !hasSuccessors || choice === LEAVE;
	const warningMessage = "The product will be left without a default recipe.";

	return (
		<MesfModal
			id="straightener-deactivate-current"
			title="Deactivate default recipe"
			open={open}
			handleClose={onCancel}
			maxWidth="sm"
		>
			<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
				<Typography sx={{ mb: 2 }}>
					<strong>{recipeLabel}</strong> is the default recipe for{" "}
					<strong>{productLabel}</strong>.
				</Typography>
				{hasSuccessors && (
					<TextField
						select
						fullWidth
						size="small"
						label="Set another recipe as default"
						value={choice}
						onChange={(e) => setChoice(e.target.value)}
						slotProps={{ inputLabel: { shrink: true } }}
					>
						{options.map((v) => (
							<MenuItem key={v.Id} value={String(v.Id)}>
								{versionLabel(v)}
							</MenuItem>
						))}
						<MenuItem value={LEAVE}>Leave without default</MenuItem>
					</TextField>
				)}
				{leavesWithoutCurrent && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							mt: hasSuccessors ? 2 : 0,
						}}
					>
						<WarningAmberIcon
							sx={{ fontSize: "1.05rem", color: "error.main" }}
						/>
						<Typography
							variant="caption"
							color="error"
							sx={{ fontWeight: 600 }}
						>
							{warningMessage}
						</Typography>
					</Box>
				)}
			</MesfModal.Content>
			<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 2,
						width: "100%",
					}}
				>
					<Button
						variant="contained"
						color="inherit"
						onClick={onCancel}
						disabled={isBusy}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						color={leavesWithoutCurrent ? "error" : "primary"}
						onClick={confirm}
						disabled={isBusy}
					>
						Deactivate
					</Button>
				</Box>
			</MesfModal.Actions>
		</MesfModal>
	);
};
